import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/vue-query';
import { computed, type Ref, unref } from 'vue';
import ApiRequest from '@/plugins/store/actions/api/ApiRequest';
import { FarmToFarmAnimalGroup, FarmToFarmPeriods, FarmToFarmReport, FarmToFarmSelection } from '@/types';

/**
 * Data access for the farm-to-farm benchmark report.
 *
 * The tree and the period list are cheap and stable; the report itself can take minutes for ten
 * farms across three years, so it is a mutation rather than a query - it should run when the
 * user asks for it and never be refetched behind their back.
 *
 * Every request here passes `skipHandleError`, because every consumer renders its own inline error
 * panel. Without it ApiClient toasts and returns undefined instead of throwing; TanStack turns that
 * undefined into an error anyway, so the failure arrives as a toast *and* a panel - and since the
 * queryFn resolves rather than rejects, each of the three default retries toasts again.
 */

const TREE_OPTIONS = {
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 10,
};

export const queryKeys = {
    root: ['reports', 'farm-to-farm'],
    locations: () => [...queryKeys.root, 'locations'],
    /**
     * The farm selection is part of the key, not just the request body.
     *
     * The list is farm-dependent - it starts at the earliest registration across the selected
     * farms - so a single static key would serve one farm set's periods to another, and the
     * ten-minute staleTime would keep doing it long after the user changed the selection.
     */
    periods: (animalGroupId: number, farmIds: number[]) => [...queryKeys.root, 'periods', animalGroupId, [...farmIds].sort((a, b) => a - b)],
};

export function useFarmToFarmLocations(options: UseQueryOptions<FarmToFarmAnimalGroup[]> = {}) {
    return useQuery<FarmToFarmAnimalGroup[]>({
        queryKey: queryKeys.locations(),
        queryFn: () => new ApiRequest({ skipHandleError: true }).send('get', 'reports/farm-to-farm/locations'),
        ...TREE_OPTIONS,
        ...options,
    });
}

/**
 * The periods the selected farms may be compared over - all four granularities in one response.
 *
 * A POST, not a GET: the earliest selectable period is the earliest registration across the
 * chosen farms, so the list cannot be computed until they are known. Returning every granularity
 * together means the period-type radio switches with no round trip.
 */
export function useFarmToFarmPeriods(animalGroupId: Ref<number | null>, farmIds: Ref<number[]>) {
    return useQuery<FarmToFarmPeriods>({
        queryKey: computed(() => queryKeys.periods(unref(animalGroupId) ?? 0, unref(farmIds))),
        queryFn: () =>
            new ApiRequest({ skipHandleError: true }).send('post', 'reports/farm-to-farm/periods', {
                animal_group_id: unref(animalGroupId),
                farm_ids: unref(farmIds),
            }),
        // Nothing to ask for until the farm picker has been through: the API rejects an empty
        // selection, so firing early would render an error panel over a step the user completed.
        enabled: computed(() => unref(animalGroupId) !== null && unref(farmIds).length > 0),
        ...TREE_OPTIONS,
    });
}

/**
 * `ApiRequest.send` returns `any`, and unlike queries, a mutation has no undefined check of its own -
 * TanStack will happily resolve one with whatever comes back. So the shape is checked at the boundary
 * rather than trusted, and a malformed 200 fails loudly instead of rendering an empty report.
 */
function isFarmToFarmReport(response: unknown): response is FarmToFarmReport {
    if (!response || typeof response !== 'object') return false;

    const report = response as FarmToFarmReport;
    return Array.isArray(report.periods) && Array.isArray(report.farms) && Array.isArray(report.groups);
}

export function useFarmToFarmReport() {
    return useMutation<FarmToFarmReport, unknown, FarmToFarmSelection>({
        mutationFn: async (payload) => {
            const response = await new ApiRequest({ skipHandleError: true }).send('post', 'reports/farm-to-farm/data', {
                animal_group_id: payload.animalGroupId,
                farm_ids: payload.farmIds,
                period_type: payload.periodType,
                periods: payload.periods,
            });

            if (!isFarmToFarmReport(response)) {
                throw new Error('Invalid farm-to-farm report response');
            }

            return response;
        },
    });
}
